import {
  Button,
  Grid,
  Group,
  NumberInput,
  Paper,
  Stack,
  Text,
  TextInput
} from '@mantine/core'
import { isNotEmpty, useForm } from '@mantine/form'
import { FormattedMessage, useIntl } from 'react-intl'
import { LabelWithHint } from '@/components/ui-shared/LabelWithHint/LabelWithHint'
import { EventDestination } from '@/types/deployment'
import { useTestConnectionStatus } from '../../useTestConnectionStatus'
import { QosSelect } from './QosSelect'
import { TestMqttConnection } from './TestMqttConnection'

type MqttBroker = {
  protocol: 'mqtt'
  url: string
  port: number
  qos: number
}

type MqttBrokerFormProps = {
  deviceId: string
  initialValues?: MqttBroker
  onSubmit: (values: MqttBroker) => void
  onCancel: () => void
}

export const MqttBrokerForm = ({
  deviceId,
  initialValues,
  onSubmit,
  onCancel
}: MqttBrokerFormProps) => {
  const intl = useIntl()

  const { checkConnection, status, errorMessage } = useTestConnectionStatus()

  const form = useForm<MqttBroker>({
    initialValues: {
      protocol: initialValues?.protocol || 'mqtt',
      url: initialValues?.url || 'localhost',
      port: initialValues?.port || 18831,
      qos: initialValues?.qos || 1
    },

    enhanceGetInputProps: () => {
      return {
        disabled: status === 'pending'
      }
    },

    validate: {
      url: isNotEmpty(
        intl.formatMessage({ id: 'integrations.mqtt.broker.url.required' })
      ),
      port: isNotEmpty(
        intl.formatMessage({ id: 'integrations.mqtt.broker.port.required' })
      )
    }
  })

  const handleTestConnection = () => {
    checkConnection({
      deviceId,
      integrationUrl: `${form.values.protocol}://${form.values.url}:${form.values.port}`,
      source: EventDestination.Mqtt
    })
  }

  return (
    <form onSubmit={form.onSubmit((values) => onSubmit(values))}>
      <Stack gap="xl">
        <Paper bg="gray.1" p="md">
          <Grid gutter="xs" mb="sm">
            <Grid.Col span="auto">
              <TextInput
                label={
                  <LabelWithHint
                    label={
                      <FormattedMessage id="integrations.mqtt.broker.url" />
                    }
                    hint={
                      <FormattedMessage id="integrations.mqtt.broker.url.hint" />
                    }
                  />
                }
                leftSection={
                  <Text size="sm" fw={500}>
                    {form.values.protocol}://
                  </Text>
                }
                leftSectionWidth={60}
                autoFocus
                {...form.getInputProps('url')}
              />
            </Grid.Col>

            <Grid.Col span="content">
              <NumberInput
                w={120}
                label={<FormattedMessage id="integrations.mqtt.broker.port" />}
                allowNegative={false}
                allowDecimal={false}
                hideControls
                {...form.getInputProps('port')}
              />
            </Grid.Col>
          </Grid>

          <TestMqttConnection
            status={status}
            errorMessage={errorMessage}
            disabled={!form.isValid()}
            onTestConnection={handleTestConnection}
          />
        </Paper>

        <Paper bg="gray.1" p="md">
          <QosSelect
            maw={300}
            value={String(form.values.qos)}
            onChange={(value) => form.setFieldValue('qos', Number(value))}
          />
        </Paper>
      </Stack>

      <Group justify="end" mt="xl">
        <Button miw={100} size="xs" variant="default" onClick={onCancel}>
          <FormattedMessage id="cancel" />
        </Button>

        <Button miw={100} type="submit" size="xs">
          <FormattedMessage id="save" />
        </Button>
      </Group>
    </form>
  )
}
